/**
 * Projectile rounds drawn as streaks (plan G8): every live round of a weapon listed in `fx.json` tracers is
 * appended to its weapon's pool for this frame: PDC rounds as `tracerPdc` (only every `tracerEvery`-th
 * round of a shooter), pulse-laser rounds as `boltLaser`. The streak's head is the round's interpolated
 * position, its axis the velocity direction, its length `speed × streakSeconds` clipped to the distance the
 * round has flown from the muzzle (a new bolt grows out of the gun instead of starting behind it).
 * Allocation-free (D-014).
 */
import { isTracer, type ProjectilePool } from '../../combat/projectiles'
import { SIM_DT } from '../../core/loop'
import type { Vec3 } from '../../core/math'
import type { WeaponsData } from '../../data/schemas'
import type { FxConfig, TracerConfig } from './config'
import { pushInstance, type ParticlePool, type ParticleSystem } from './particles'

const GOLDEN = 0.6180339887498949

export interface ProjectileView {
  /** Per weapon index: the pool its rounds draw into, or null (no tracer). */
  readonly pools: readonly (ParticlePool | null)[]
  /** Per weapon index: the tracer tuning (live `fx.json` object), or null. */
  readonly tracers: readonly (TracerConfig | null)[]
  /** Rounds drawn this frame. */
  drawn: number
}

export function createProjectileView(weapons: WeaponsData, tracers: FxConfig['tracers'], sys: ParticleSystem): ProjectileView {
  const pools: (ParticlePool | null)[] = []
  const cfgs: (TracerConfig | null)[] = []
  for (const w of weapons.weapons) {
    const t = w.kind === 'projectile' ? tracers[w.id] : undefined
    pools.push(t ? sys.pools[t.material] : null)
    cfgs.push(t ?? null)
  }
  return { pools, tracers: cfgs, drawn: 0 }
}

/**
 * Streak length for a round flying at `speed` (m/s) whose weapon has muzzle speed `muzzleSpeed`, after
 * `flight` seconds: `speed × streakSeconds`, at most the distance flown relative to the shooter.
 */
export function streakLength(speed: number, muzzleSpeed: number, flight: number, streakSeconds: number): number {
  const full = speed * streakSeconds
  const flown = muzzleSpeed * (flight > 0 ? flight : 0)
  return full < flown ? full : flown
}

/**
 * Appends every visible round to its pool. `alpha` interpolates between the round's previous and current
 * tick positions; `cam` is the float64 render origin (D-011). Call between `beginParticleFrame` and
 * `endParticleFrame`.
 */
export function drawProjectiles(view: ProjectileView, pool: ProjectilePool, weapons: WeaponsData, alpha: number, cam: Vec3): void {
  let drawn = 0
  const back = (1 - alpha) * SIM_DT
  for (let i = 0; i < pool.high; i++) {
    if (pool.alive[i] !== 1) continue
    const wi = pool.weapon[i]
    const target = view.pools[wi]
    const cfg = view.tracers[wi]
    const w = weapons.weapons[wi]
    if (!target || !cfg || !w || w.kind !== 'projectile') continue
    if (!isTracer(pool, i, w.tracerEvery)) continue
    const vx = pool.vx[i]
    const vy = pool.vy[i]
    const vz = pool.vz[i]
    const speed = Math.sqrt(vx * vx + vy * vy + vz * vz)
    if (speed < 1e-6) continue
    const dx = vx / speed
    const dy = vy / speed
    const dz = vz / speed
    const life = w.range / w.speed
    const flight = life - pool.ttl[i] - back
    const len = streakLength(speed, w.speed, flight, cfg.streakSeconds)
    const hx = pool.px[i] + (pool.x[i] - pool.px[i]) * alpha
    const hy = pool.py[i] + (pool.y[i] - pool.py[i]) * alpha
    const hz = pool.pz[i] + (pool.z[i] - pool.pz[i]) * alpha
    const half = 0.5 * len
    let age = life > 0 ? (cfg.fade * flight) / life : 0
    age = age < 0 ? 0 : age > 1 ? 1 : age
    const ok = pushInstance(
      target,
      Math.fround(hx - cam.x - dx * half),
      Math.fround(hy - cam.y - dy * half),
      Math.fround(hz - cam.z - dz * half),
      age,
      (i * GOLDEN) % 1,
      1,
      1,
      1,
      cfg.width,
      len,
      dx,
      dy,
      dz,
    )
    if (ok) drawn++
  }
  view.drawn = drawn
}
